import { useCallback, useEffect, useRef, useState } from 'react'

/** What the caller renders as "Saving…" / "Saved" / "Couldn't save". */
export type AutosaveState = 'idle' | 'pending' | 'saving' | 'saved' | 'error'

interface UseDebouncedAutosaveOptions<T> {
  /** The value to persist. Compared by `serialize` to decide if a save is needed. */
  value: T
  /** Performs the write. Rejections surface as `state === 'error'`. */
  save: (value: T) => Promise<void>
  /** Quiet period before a write. Default 1500ms. */
  delay?: number
  /** While false, changes are tracked but nothing is written. */
  enabled?: boolean
  /**
   * Stable string for `value`. The default is `JSON.stringify`; override to
   * exclude fields that must not trigger a save (or that are not serializable).
   */
  serialize?: (value: T) => string
}

export interface UseDebouncedAutosaveResult {
  state: AutosaveState
  /** Message from the most recent failure; cleared by the next success. */
  error: string | null
  /** When the last successful save landed. */
  lastSavedAt: Date | null
  /**
   * Write immediately, bypassing the debounce, and resolve when it lands.
   * Call this before navigating or submitting so a pending edit is not lost.
   *
   * Resolves (rather than throwing) even on failure — a rejection from a
   * background timer is an unhandled rejection. It resolves to `true` when
   * there was nothing to save or the save succeeded, and `false` when it
   * failed. 🔴 A caller doing something irreversible afterwards must check the
   * RETURN VALUE, not `state`: `state` at the await point is the value from
   * the render that started the flush, not the one the flush produced.
   */
  flush: () => Promise<boolean>
}

/**
 * Debounced, serialized autosave.
 *
 * Three properties matter, and all three exist because of bugs this pattern
 * prevents rather than because they are elegant:
 *
 * 1. **Debounced** — a ~70-field form must not fire a request per keystroke.
 *    The timer restarts on every change and only fires after `delay` of quiet.
 *
 * 2. **Serialized** — at most one write is ever in flight. A second change that
 *    arrives mid-write does not race it; it is queued and runs after, so the
 *    last value typed is always the last value written. Two concurrent PUTs of
 *    an upsert can otherwise land out of order and resurrect stale text.
 *
 * 3. **Change-gated** — a save only happens when `serialize(value)` differs from
 *    what was last written. Without this, an unrelated re-render (or a
 *    StrictMode double-mount) re-saves an unchanged form forever.
 *
 * The saved snapshot lives in a ref, so a StrictMode remount does not re-send
 * what the first mount already wrote.
 */
export const useDebouncedAutosave = <T,>({
  value,
  save,
  delay = 1500,
  enabled = true,
  serialize = (input: T) => JSON.stringify(input),
}: UseDebouncedAutosaveOptions<T>): UseDebouncedAutosaveResult => {
  const [state, setState] = useState<AutosaveState>('idle')
  const [error, setError] = useState<string | null>(null)
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null)

  // Latest inputs, read at fire time rather than captured, so the timer always
  // writes the newest value and `save` can be an inline closure.
  const valueRef = useRef(value)
  const saveRef = useRef(save)
  const serializeRef = useRef(serialize)
  valueRef.current = value
  saveRef.current = save
  serializeRef.current = serialize

  /** Serialized form of the last value successfully written. */
  const savedSnapshotRef = useRef<string | null>(null)
  /** The write currently in flight, so a second caller can await it. */
  const inFlightRef = useRef<Promise<void> | null>(null)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Deliberately NOT an effect-scoped `active` flag. StrictMode tears the effect
  // down and back up, so an effect-scoped flag is already false when the one
  // in-flight request resolves, and every result would be silently dropped.
  const mountedRef = useRef(true)
  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  const runSave = useCallback(async (): Promise<boolean> => {
    // Queue behind whatever is already running rather than racing it.
    if (inFlightRef.current) {
      await inFlightRef.current.catch(() => undefined)
    }

    const snapshot = serializeRef.current(valueRef.current)
    if (snapshot === savedSnapshotRef.current) return true

    const current = valueRef.current
    if (mountedRef.current) setState('saving')

    // The OUTCOME, reported to the caller.
    //
    // 🔴 `runSave` deliberately resolves rather than rejects — a rejection from
    // a background debounce timer is an unhandled promise rejection, and the
    // failure is already surfaced through `state` for the save indicator. But a
    // caller that FLUSHES before doing something irreversible (leaving the
    // wizard, submitting) needs to know, and reading `state` right after
    // `await` gives it the value from the render that started the flush, not
    // the one the flush produced. So the boolean travels back with the promise.
    let succeeded = true

    const attempt = (async () => {
      try {
        await saveRef.current(current)
        savedSnapshotRef.current = snapshot
        if (mountedRef.current) {
          setState('saved')
          setError(null)
          setLastSavedAt(new Date())
        }
      } catch (err) {
        // Leave the snapshot unchanged so the next change retries this content.
        succeeded = false
        if (mountedRef.current) {
          setState('error')
          setError(err instanceof Error ? err.message : 'Could not save your progress')
        }
      }
    })()

    inFlightRef.current = attempt
    await attempt
    if (inFlightRef.current === attempt) inFlightRef.current = null
    return succeeded
  }, [])

  const serialized = serialize(value)

  useEffect(() => {
    if (!enabled) return
    if (serialized === savedSnapshotRef.current) return

    setState(prev => (prev === 'saving' ? prev : 'pending'))

    if (timerRef.current) clearTimeout(timerRef.current)
    timerRef.current = setTimeout(() => {
      void runSave()
    }, delay)

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [serialized, enabled, delay, runSave])

  /**
   * Save now, and report whether it worked.
   *
   * Returns `true` when there was nothing to save or the save succeeded, and
   * `false` when it failed. Callers doing something irreversible afterwards
   * must check it — `state` is stale at the await point (see `succeeded`).
   */
  const flush = useCallback(async (): Promise<boolean> => {
    if (timerRef.current) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
    return runSave()
  }, [runSave])

  return { state, error, lastSavedAt, flush }
}
